import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { CapsuleButton } from "../../../Everou/_components";
import { Selectors } from "../../../Everou/_reducers/app.reducer";
import { TimeHelper } from "../../../Everou/_helpers/TimeHelper";
import { accountDraftOf, Integration, IntegrationAccountDraft, IntegrationCompany, SyncStatusValue } from "../../../Everou/_modelsTS/Integration";
import { IntegrationTemplateRef, PMSNavigationActions } from "../../_actions/PMSNavigationActions";
import { useIntegrationSync } from "../_hooks/useIntegrationSync";

type Props = {
    integration: Integration;
    // Where the account is looked at in place instead of navigating to it
    // (a location outside any organization) -- same as the row's own button.
    onBrowseAccount?: (
        company: IntegrationCompany,
        template: IntegrationTemplateRef | null,
        draft?: IntegrationAccountDraft | null,
    ) => void;
    // Attach the integration to one of the accounts at hand (the row's
    // "assign account" action), for an integration hanging from none: its
    // credentials live on the row itself, and there is no account to edit.
    onAssignAccount?: () => void;
    // Whether the viewer's accounts are known yet: until then there is no
    // telling whether the account is theirs, nor whether one exists to
    // assign instead of creating another.
    accountsLoaded?: boolean;
    // The integration hangs from a colleague's personal account: nobody but
    // them can open it, so there is nowhere to send the viewer.
    isForeignAccount?: boolean;
    locationUid: string;
};

// can_force_sync === false companies that receive data via webhook push. The
// rest of the non-force-sync ones are developer/external-app integrations.
const WEBHOOK_COMPANIES: IntegrationCompany[] = [
    IntegrationCompany.LODGIFY,
    IntegrationCompany.ENSO_CONNECT,
];

// Health of an integration within a location. Only the pull-based integrations
// (can_force_sync) get a sync badge + manual "sync now" button; the rest just
// get a one-line note explaining how they stay in sync. Self-contained: it
// fires the sync and polls until it resolves.
export function IntegrationSyncStatus({
    integration,
    locationUid,
    onBrowseAccount,
    onAssignAccount,
    accountsLoaded = true,
    isForeignAccount = false,
}: Props) {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useSelector(Selectors.getPMSLocationInfo);
    const { isSyncing, onSync } = useIntegrationSync(integration, locationUid);

    // The API key lives on the integration template, edited where the
    // templates are managed — not in this per-location config. Send the user
    // there, to THIS integration's account: the company's panel alone lists
    // the viewer's accounts, which for a location outside any organization
    // are not necessarily the colleague's the integration hangs from.
    // Not when it hangs from a colleague's personal account, though: that one
    // opens for nobody but them, so the row just says whose it is to fix.
    // Hanging from none (an orphan row carrying its own credentials), the
    // fix is to attach it to an account -- or to create one when there is
    // none to attach it to, prefilled with the row's own credentials so they
    // are not typed twice (BAC-672).
    const template: IntegrationTemplateRef | null = integration.templateUid
        ? { uid: integration.templateUid, name: integration.templateName }
        : null;
    const assignsAccount = !template && !!onAssignAccount;
    const goToIntegrations = () => {
        if (assignsAccount) return onAssignAccount();
        const draft = template ? null : accountDraftOf(integration);
        return onBrowseAccount
            ? onBrowseAccount(integration.company, template, draft)
            : dispatch(PMSNavigationActions.goToIntegrationTemplate(integration.company, template, draft));
    };
    const invalidCredentialsText = assignsAccount
        ? t('mod_integrations_sync_invalid_credentials_assign')
        : template
            ? t('mod_integrations_sync_invalid_credentials')
            : t('mod_integrations_sync_invalid_credentials_create');

    if (!integration?.uid) return null;

    return (
        <div className="col-span-full mt-2 pt-2 border-top">
            {integration.canForceSync ? forceSyncView() : autoSyncNote()}
        </div>
    );

    // Pull-based: sync badge + "sync now" + (if needed) the dead-credentials CTA.
    function forceSyncView() {
        const running = isSyncing || integration.syncStatus === SyncStatusValue.RUNNING;

        return (
            <>
                <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: "8px" }}>
                    <div className="small">{statusBadge(running)}</div>
                    <div>
                        <CapsuleButton
                            size="SMALL"
                            isEnabled={!running}
                            onClick={onSync}
                            text={running ? t('mod_integrations_syncing') : t('mod_integrations_sync_now')}
                        />
                    </div>
                </div>
                {integration.apikeyIsValid === false && accountsLoaded && isForeignAccount && (
                    <div className="mt-2 small text-danger">
                        {`⚠️ ${t('mod_integrations_sync_invalid_credentials_foreign')}`}
                    </div>
                )}
                {integration.apikeyIsValid === false && accountsLoaded && !isForeignAccount && (
                    <div className="mt-2">
                        <CapsuleButton
                            // eslint-disable-next-line react/style-prop-object
                            style="DANGER"
                            size="SMALL"
                            textUppercase={false}
                            onClick={goToIntegrations}
                            text={`⚠️ ${invalidCredentialsText} ›`}
                        />
                    </div>
                )}
            </>
        );
    }

    // Non-force-sync: no badge (it would sit at idle forever), just a note.
    function autoSyncNote() {
        // Manual is not "kept in sync" by anyone: it has no provider and no
        // webhook, the manager types the reservations in. Without its own
        // branch it fell through to the developer note, which says an external
        // app manages it -- the opposite of what it is.
        if (integration.company === IntegrationCompany.MANUAL) {
            return (
                <span className="text-muted small">
                    {t('mod_integrations_sync_manual')}
                </span>
            );
        }

        const note = WEBHOOK_COMPANIES.includes(integration.company)
            ? t('mod_integrations_sync_webhook')
            : t('mod_integrations_sync_developer');

        return <span className="text-muted small">{note}</span>;
    }

    function statusBadge(running: boolean) {
        if (running) {
            return (
                <span className="d-flex align-items-center text-info">
                    <span className="spinner-border spinner-border-sm mr-2" />
                    {t('mod_integrations_syncing')}
                </span>
            );
        }

        switch (integration.syncStatus) {
            case SyncStatusValue.OK:
                return (
                    <span className="text-success">
                        ● {t('mod_integrations_sync_last', { date: formatDate(integration.lastSyncAt), interpolation: { escapeValue: false } })}
                    </span>
                );

            case SyncStatusValue.ERROR:
                return (
                    <span className="text-danger">
                        ● {t('mod_integrations_sync_error', { error: integration.syncError ?? '', interpolation: { escapeValue: false } })}
                    </span>
                );

            case SyncStatusValue.IDLE:
            default:
                return <span className="text-muted">● {t('mod_integrations_sync_not_yet')}</span>;
        }
    }

    function formatDate(date?: string) {
        if (!date) return '';
        return TimeHelper.localizeIsoStringToFormat(
            date,
            location?.timezone,
            TimeHelper.getTimeFormats().DATE_NO_SECONDS,
        );
    }
}
